import { Lock } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useSessionUser } from "@/hooks/useSessionUser";
import { planLabel } from "@/lib/plans";
import { LockedBadge } from "@/components/locked/LockedBadge";
import LockedComponentPlaceholder from "@/components/locked/LockedComponentPlaceholder";
import { getLockedFeatureCopy } from "@/components/locked/featureCopy";
import type { CapabilityFlag } from "@/types/capabilities";

type CapabilityProp = CapabilityFlag | string | Array<CapabilityFlag | string>;
type CapabilityVariant = "compact" | "card" | "hide";

interface CapabilityGateProps {
  flag?: CapabilityProp;
  capability?: CapabilityProp;
  children: ReactNode;
  fallback?: ReactNode;
  variant?: CapabilityVariant;
}

function DefaultLockedCard({ flag }: { flag?: CapabilityFlag | string }) {
  const copy = flag ? getLockedFeatureCopy(flag) : undefined;
  const title = copy?.title ?? "Upgrade to unlock";
  const description =
    copy?.description ?? "This capability is part of the Pro toolkit with automation, routing, and CRM accelerators.";
  const feature = copy?.featureName ?? flag ?? title;

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-background/50 p-4 text-sm text-foreground">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10">
          <Lock className="h-4 w-4 text-primary-foreground dark:text-amber-200" />
        </div>
        <div className="flex-1 space-y-1">
          <p className="text-xs uppercase tracking-wide text-amber-300">Locked capability</p>
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="flex justify-end">
        <LockedBadge feature={feature} />
      </div>
    </div>
  );
}

export function CapabilityGate({
  flag,
  capability,
  children,
  fallback = null,
  variant = "compact",
}: CapabilityGateProps) {
  const { capabilities, hasFullAccess, ready, tier } = useCapabilities();
  const { role } = useSessionUser();
  const entries = flag ?? capability;
  const normalized = (Array.isArray(entries) ? entries : [entries]).filter(
    (entry): entry is CapabilityFlag | string => Boolean(entry),
  );
  const hasEntitlements = normalized.length === 0 || normalized.some((entry) => (entry in capabilities ? Boolean(capabilities[entry]) : false));
  const allowed = hasEntitlements || hasFullAccess;
  const fallbackFlag = normalized.find((entry) => typeof entry === "string");
  if (!ready && normalized.length > 0) {
    return (
      <div className="rounded-2xl border border-border bg-background/40 p-4 text-sm text-muted-foreground">
        <p>Loading entitlements for plan {planLabel(tier)} (role {role ?? "—"})…</p>
      </div>
    );
  }
  if (allowed) {
    return <>{children}</>;
  }
  const gateVariant = variant ?? "compact";
  if (gateVariant === "hide") {
    return null;
  }
  if (gateVariant === "card") {
    return <>{fallback ?? <DefaultLockedCard flag={fallbackFlag} />}</>;
  }
  if (gateVariant === "compact") {
    if (fallback === null) {
      return null;
    }
    return <LockedComponentPlaceholder flag={fallbackFlag ?? undefined} />;
  }
  const requestedInfo = normalized.length > 0 ? normalized.join(", ") : "n/a";
  const fallbackNode = fallback ?? <DefaultLockedCard flag={fallbackFlag} />;
  return (
    <>
      {fallbackNode}
      {import.meta.env.DEV && (
        <div className="mt-3 rounded-2xl border border-rose-500/20 bg-rose-500/5 p-3 text-xs text-rose-100">
          <p className="text-rose-200">Capability gate blocked</p>
          <p>Requested: {requestedInfo}</p>
          <p>
            Role: {role ?? "—"} | Plan: {planLabel(tier)} | Capability value:{" "}
            {requestedInfo === "n/a" ? "—" : String(capabilities[normalized[0]] ?? "false")}
          </p>
          <p>Provider ready: {ready ? "yes" : "no"}</p>
        </div>
      )}
    </>
  );
}

export default CapabilityGate;
